if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]:not([id^="GalleryThumbnailsVertical"])'),
          verticalThumbnails: this.querySelector('[id^="GalleryThumbnailsVertical"]'),
          thumbnailScrollContainer: this.querySelector('[id^="ThumbnailScrollContainer"]'),
          thumbnailList: this.querySelector('[id^="ThumbnailListVertical"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');

        this.initVerticalThumbnails();
        this.initHorizontalThumbnails();
        this.initMobileSwipeSync();
        this.syncThumbnailHeight();

        // Reveal vertical thumbnails after initialization (prevents flash)
        if (this.elements.verticalThumbnails) {
          this.elements.verticalThumbnails.classList.add('is-initialized');
        }

        // Scroll to active slide on page load (instant, not smooth)
        this.scrollToActiveSlide();

        // Preload full-size image for faster zoom
        this.preloadActiveImage();

        // Initialize mobile zoom lightbox (intercepts clicks on mobile)
        this.initMobileZoom();

        // On mobile, move variant picker above price
        if (!this.mql.matches) {
          this.swapVariantPickerAbovePrice();
        }

        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      initVerticalThumbnails() {
        if (!this.elements.verticalThumbnails) return;

        const upBtn = this.elements.verticalThumbnails.querySelector('.thumbnail-arrow--up');
        const downBtn = this.elements.verticalThumbnails.querySelector('.thumbnail-arrow--down');
        const scrollContainer = this.elements.thumbnailScrollContainer;

        if (upBtn && scrollContainer) {
          upBtn.addEventListener('click', () => {
            scrollContainer.scrollBy({ top: -100, behavior: 'smooth' });
          });
        }

        if (downBtn && scrollContainer) {
          downBtn.addEventListener('click', () => {
            scrollContainer.scrollBy({ top: 100, behavior: 'smooth' });
          });
        }

        if (scrollContainer) {
          scrollContainer.addEventListener('scroll', () => this.updateArrowStates());
          requestAnimationFrame(() => this.updateArrowStates());
        }

        // Thumbnail click handlers
        this.elements.verticalThumbnails.querySelectorAll('[data-target]').forEach((thumb) => {
          thumb.querySelector('button').addEventListener('click', () => {
            this.setActiveMedia(thumb.dataset.target, false);
          });
        });
      }

      initHorizontalThumbnails() {
        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.bindThumbnailClickHandlers();
      }

      /**
       * Bind click handlers to horizontal thumbnail buttons.
       * Called on init and after thumbnail HTML is replaced on variant change.
       */
      bindThumbnailClickHandlers() {
        if (!this.elements.thumbnails) return;
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          const btn = mediaToSwitch.querySelector('button');
          if (!btn) return;
          const newBtn = btn.cloneNode(true);
          btn.parentNode.replaceChild(newBtn, btn);
          newBtn.addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
      }

      /**
       * Bind click handlers to vertical thumbnail buttons.
       * Called after vertical thumbnail HTML is replaced on variant change.
       */
      bindVerticalThumbnailClickHandlers() {
        if (!this.elements.thumbnailList) return;
        this.elements.thumbnailList.querySelectorAll('[data-target]').forEach((thumb) => {
          const btn = thumb.querySelector('button');
          if (!btn) return;
          const newBtn = btn.cloneNode(true);
          btn.parentNode.replaceChild(newBtn, btn);
          newBtn.addEventListener('click', () => {
            this.setActiveMedia(thumb.dataset.target, false);
          });
        });
      }

      initMobileSwipeSync() {
        if (!this.elements.viewer) return;
        const mediaList = this.elements.viewer.querySelector('.product__media-list');
        if (!mediaList) return;

        let scrollTimer = null;
        mediaList.addEventListener('scroll', () => {
          if (scrollTimer) clearTimeout(scrollTimer);
          scrollTimer = setTimeout(() => {
            const items = mediaList.querySelectorAll('[data-media-id]');
            const scrollLeft = mediaList.scrollLeft;
            let closestItem = null;
            let closestDistance = Infinity;

            items.forEach((item) => {
              const distance = Math.abs(item.offsetLeft - scrollLeft);
              if (distance < closestDistance) {
                closestDistance = distance;
                closestItem = item;
              }
            });

            if (closestItem) {
              const mediaId = closestItem.dataset.mediaId;
              items.forEach((item) => item.classList.remove('is-active'));
              closestItem.classList.add('is-active');

              // Sync vertical thumbnails
              const vertThumb = this.elements.thumbnailList?.querySelector(`[data-target="${mediaId}"]`);
              if (vertThumb) this.setActiveVerticalThumbnail(vertThumb);

              // Sync horizontal thumbnails
              const horizThumb = this.elements.thumbnails?.querySelector(`[data-target="${mediaId}"]`);
              if (horizThumb) this.setActiveThumbnail(horizThumb);

              // Auto-play video if swiped to a video slide
              this.playActiveMedia(closestItem);

              // Sync gallery + thumbnail height
              this.syncGalleryHeight();
            }
          }, 100);
        }, { passive: true });
      }

      syncThumbnailHeight() {
        if (!this.elements.verticalThumbnails || !this.elements.viewer) return;

        const sync = () => {
          // Find the currently visible slide (scroll position based)
          const mediaList = this.elements.viewer.querySelector('.product__media-list');
          let activeSlide = null;

          if (mediaList) {
            const items = mediaList.querySelectorAll('.product__media-item');
            const scrollLeft = mediaList.scrollLeft;
            let closestDist = Infinity;
            items.forEach((item) => {
              const dist = Math.abs(item.offsetLeft - scrollLeft);
              if (dist < closestDist) {
                closestDist = dist;
                activeSlide = item;
              }
            });
          }

          if (!activeSlide) {
            activeSlide = this.elements.viewer.querySelector('.product__media-item.is-active');
          }

          if (activeSlide) {
            const mediaContainer = activeSlide.querySelector('.product-media-container');
            if (mediaContainer) {
              let height;
              const isVideoSlide = activeSlide.classList.contains('product__media-item--full');
              const containerWidth = mediaList ? mediaList.clientWidth : this.elements.viewer.clientWidth;

              if (isVideoSlide) {
                // For video slides, calculate height from the container's CSS aspect ratio
                // to avoid flicker from Dawn's padding-based sizing
                const ratio = parseFloat(mediaContainer.style.getPropertyValue('--ratio')) || (16/9);
                height = containerWidth / ratio;
              } else {
                // For image slides, use the actual rendered height
                const img = mediaContainer.querySelector('img');
                if (img && img.naturalHeight && img.naturalWidth) {
                  height = containerWidth / (img.naturalWidth / img.naturalHeight);
                } else {
                  height = mediaContainer.offsetHeight;
                }
                if (height <= 0) height = mediaContainer.scrollHeight;
              }

              // Set both the media list height and thumbnail column height
              if (mediaList && height > 0) {
                mediaList.style.height = `${height}px`;
                mediaList.style.transition = 'height 0.3s ease';
              }
              if (height > 0) {
                this.elements.verticalThumbnails.style.height = `${height}px`;
                this.elements.verticalThumbnails.style.transition = 'height 0.3s ease';
              }
            }
          }
          this.updateArrowStates();
        };

        // Sync on resize
        const observer = new ResizeObserver(debounce(sync, 100));
        observer.observe(this.elements.viewer);

        // Sync on scroll (during slide transitions)
        const mediaList = this.elements.viewer.querySelector('.product__media-list');
        if (mediaList) {
          mediaList.addEventListener('scroll', debounce(sync, 50), { passive: true });
        }

        // Sync after images load
        this.elements.viewer.querySelectorAll('img').forEach((img) => {
          if (img.complete) {
            sync();
          } else {
            img.addEventListener('load', sync, { once: true });
          }
        });

        requestAnimationFrame(sync);
        setTimeout(sync, 500);
        setTimeout(sync, 1000);
      }

      updateArrowStates() {
        const container = this.elements.thumbnailScrollContainer;
        if (!container) return;

        const upBtn = this.elements.verticalThumbnails.querySelector('.thumbnail-arrow--up');
        const downBtn = this.elements.verticalThumbnails.querySelector('.thumbnail-arrow--down');
        const list = this.elements.thumbnailList;

        if (!upBtn || !downBtn || !list) return;

        const scrollTop = container.scrollTop;
        const scrollHeight = list.scrollHeight;
        const containerHeight = container.clientHeight;

        // Hide arrows entirely if all thumbnails fit
        const allFit = scrollHeight <= containerHeight + 2;
        upBtn.style.display = allFit ? 'none' : '';
        downBtn.style.display = allFit ? 'none' : '';

        if (!allFit) {
          upBtn.style.display = scrollTop <= 1 ? 'none' : '';
          downBtn.style.display = (scrollTop + containerHeight >= scrollHeight - 1) ? 'none' : '';
        }
      }

      scrollToActiveSlide() {
        const activeSlide = this.elements.viewer?.querySelector('.product__media-item.is-active');
        if (activeSlide) {
          requestAnimationFrame(() => {
            activeSlide.parentElement.scrollTo({ left: activeSlide.offsetLeft, behavior: 'instant' });
          });
        }
      }

      preloadActiveImage() {
        // Preload full-size image for faster zoom
        const activeSlide = this.elements.viewer?.querySelector('.product__media-item.is-active');
        if (activeSlide) {
          const img = activeSlide.querySelector('.product__media img');
          if (img && img.src) {
            const preload = new Image();
            preload.src = img.src.replace(/width=\d+/, 'width=1946');
          }
        }
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails?.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        if (thumbnail) this.setActiveThumbnail(thumbnail);

        // Also update vertical thumbnails
        const vertThumb = this.elements.thumbnailList?.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        if (vertThumb) this.setActiveVerticalThumbnail(vertThumb);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) return;

        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia.classList.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            if (activeThumbnail) {
              activeThumbnail.parentElement.firstChild !== activeThumbnail &&
                activeThumbnail.parentElement.prepend(activeThumbnail);
            }
          }

          // Also prepend in vertical thumbnails
          if (this.elements.thumbnailList) {
            const vertThumb = this.elements.thumbnailList.querySelector(`[data-target="${mediaId}"]`);
            if (vertThumb) {
              vertThumb.parentElement.firstChild !== vertThumb && vertThumb.parentElement.prepend(vertThumb);
            }
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          // Smooth horizontal slide to the active media
          activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft, behavior: 'smooth' });

          const activeMediaRect = activeMedia.getBoundingClientRect();
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        // Update horizontal thumbnails
        if (this.elements.thumbnails) {
          const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
          this.setActiveThumbnail(activeThumbnail);
          if (activeThumbnail) {
            this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
          }
        }

        // Update vertical thumbnails
        const vertThumb = this.elements.thumbnailList?.querySelector(`[data-target="${mediaId}"]`);
        if (vertThumb) this.setActiveVerticalThumbnail(vertThumb);

        // Re-sync thumbnail column height
        this.syncThumbnailHeight();

        // Preload full-size image for faster zoom
        this.preloadActiveImage();
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      setActiveVerticalThumbnail(thumbnail) {
        if (!this.elements.thumbnailList || !thumbnail) return;

        this.elements.thumbnailList.querySelectorAll('.thumbnail-list-vertical__item').forEach((item) => {
          item.classList.remove('is-active');
          item.querySelector('button')?.removeAttribute('aria-current');
        });
        thumbnail.classList.add('is-active');
        thumbnail.querySelector('button')?.setAttribute('aria-current', 'true');

        // Scroll thumbnail into view within the scroll container
        const container = this.elements.thumbnailScrollContainer;
        if (container) {
          const thumbTop = thumbnail.offsetTop;
          const thumbHeight = thumbnail.offsetHeight;
          const scrollTop = container.scrollTop;
          const containerHeight = container.clientHeight;

          if (thumbTop < scrollTop) {
            container.scrollTo({ top: thumbTop, behavior: 'smooth' });
          } else if (thumbTop + thumbHeight > scrollTop + containerHeight) {
            container.scrollTo({ top: thumbTop + thumbHeight - containerHeight, behavior: 'smooth' });
          }
        }
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) {
          deferredMedia.loadContent(false);

          // Auto-play and quickly hide controls
          setTimeout(() => {
            const video = deferredMedia.querySelector('video');
            if (video) {
              video.muted = true;
              video.play().catch(() => {});
              // Hide controls as fast as possible
              setTimeout(() => video.removeAttribute('controls'), 300);
              // Re-show on tap, hide again after
              if (!video._tapBound) {
                video._tapBound = true;
                video.addEventListener('click', () => {
                  video.setAttribute('controls', 'true');
                  clearTimeout(video._hideTimer);
                  video._hideTimer = setTimeout(() => video.removeAttribute('controls'), 3000);
                });
              }
            }
          }, 200);
        }
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      swapVariantPickerAbovePrice() {
        const infoContainer = document.querySelector('.product__info-container');
        if (!infoContainer) return;
        const priceBlock = infoContainer.querySelector('[id^="price-"]');
        const variantPicker = infoContainer.querySelector('variant-selects');
        if (priceBlock && variantPicker) {
          priceBlock.parentNode.insertBefore(variantPicker, priceBlock);
        }
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }

      initMobileZoom() {
        // On mobile (or desktop at mobile width), intercept image clicks to open our custom lightbox
        this.elements.viewer.querySelectorAll('.product__media-toggle').forEach((btn) => {
          btn.addEventListener('click', (e) => {
            if (this.mql.matches) return;
            e.preventDefault();
            e.stopPropagation();
            const slide = btn.closest('.product__media-item');
            if (slide) this.openMobileZoom(slide);
          }, true);
        });

        this.elements.viewer.querySelectorAll('modal-opener').forEach((opener) => {
          opener.addEventListener('click', (e) => {
            if (this.mql.matches) return;
            e.preventDefault();
            e.stopPropagation();
            const slide = opener.closest('.product__media-item');
            if (slide) this.openMobileZoom(slide);
          }, true);
        });

        // Video slides: double-tap to open zoom lightbox (no secondary zoom for video)
        this.elements.viewer.querySelectorAll('.product__media-item--full').forEach((videoSlide) => {
          let lastVideoTap = 0;
          videoSlide.addEventListener('touchend', (e) => {
            if (this.mql.matches) return;
            const now = Date.now();
            if (now - lastVideoTap < 350) {
              e.preventDefault();
              this.openMobileZoom(videoSlide);
            }
            lastVideoTap = now;
          });
          // Mouse double-click for desktop at mobile width
          videoSlide.addEventListener('dblclick', (e) => {
            if (this.mql.matches) return;
            e.preventDefault();
            this.openMobileZoom(videoSlide);
          });
        });
      }

      getVisibleSlides() {
        // Get all currently visible (not display:none) slides in order
        const mediaList = this.elements.viewer.querySelector('.product__media-list');
        if (!mediaList) return [];
        return Array.from(mediaList.querySelectorAll('.product__media-item'));
      }

      openMobileZoom(activeSlide) {
        const slides = this.getVisibleSlides();
        const startIndex = slides.indexOf(activeSlide);
        if (startIndex === -1) return;

        const overlay = document.createElement('div');
        overlay.className = 'mobile-zoom-overlay';

        // Build swipeable slide container with all visible media
        let slidesHtml = '';
        slides.forEach((slide, i) => {
          const img = slide.querySelector('.product__media img');
          const isVideo = slide.classList.contains('product__media-item--full');
          const deferredMedia = slide.querySelector('.deferred-media');

          if (isVideo && deferredMedia) {
            // Try to get video source from loaded video first, then from template
            let videoEl = deferredMedia.querySelector('video');
            let videoSrc = '';
            let sourceTags = '';

            if (videoEl) {
              // Video already loaded in DOM
              videoEl.querySelectorAll('source').forEach(s => {
                sourceTags += `<source src="${s.getAttribute('src')}" type="${s.getAttribute('type')}">`;
              });
              if (!sourceTags) videoSrc = videoEl.getAttribute('src') || '';
            } else {
              // Try template content
              const tpl = deferredMedia.querySelector('template');
              if (tpl) {
                const tplVideo = tpl.content.querySelector('video');
                if (tplVideo) {
                  tplVideo.querySelectorAll('source').forEach(s => {
                    sourceTags += `<source src="${s.getAttribute('src')}" type="${s.getAttribute('type')}">`;
                  });
                  if (!sourceTags) videoSrc = tplVideo.getAttribute('src') || '';
                }
              }
            }

            slidesHtml += `<div class="mobile-zoom-slide${i === startIndex ? ' is-active' : ''}" data-index="${i}" data-type="video">
              <div class="mobile-zoom-video-container">
                <video playsinline autoplay loop controls ${sourceTags ? '' : `src="${videoSrc}"`}>
                  ${sourceTags}
                </video>
              </div>
            </div>`;
          } else if (img) {
            const fullSrc = img.src.replace(/width=\d+/, 'width=1946');
            slidesHtml += `<div class="mobile-zoom-slide${i === startIndex ? ' is-active' : ''}" data-index="${i}" data-type="image">
              <img class="mobile-zoom-image" src="${fullSrc}" alt="${img.alt || ''}" draggable="false">
            </div>`;
          }
        });

        overlay.innerHTML = `
          <div class="mobile-zoom-toolbar">
            <button class="mobile-zoom-btn mobile-zoom-magnify" aria-label="Toggle zoom">
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none"><circle cx="9" cy="9" r="6" stroke="currentColor" stroke-width="1.5"/><path d="M13.5 13.5L17 17" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/><path d="M9 6.5V11.5M6.5 9H11.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>
            </button>
            <button class="mobile-zoom-btn mobile-zoom-close" aria-label="Close">
              <svg width="18" height="18" viewBox="0 0 18 18" fill="none"><path d="M1 1L17 17M17 1L1 17" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>
            </button>
          </div>
          <div class="mobile-zoom-slider">
            ${slidesHtml}
          </div>
        `;

        document.body.appendChild(overlay);
        document.body.style.overflow = 'hidden';

        const slider = overlay.querySelector('.mobile-zoom-slider');
        const closeBtn = overlay.querySelector('.mobile-zoom-close');
        const magnifyBtn = overlay.querySelector('.mobile-zoom-magnify');
        const allSlides = overlay.querySelectorAll('.mobile-zoom-slide');

        let currentIndex = startIndex;
        let isZoomed = false;
        let isDragging = false;
        let startX = 0, startY = 0, scrollLeftStart = 0, scrollTopStart = 0;
        let lastTap = 0;
        let swipeStartX = 0, swipeStartY = 0, swiping = false;

        // Scroll to starting slide and auto-play videos (keep controls visible in lightbox)
        requestAnimationFrame(() => {
          slider.scrollLeft = startIndex * slider.clientWidth;
          allSlides.forEach(s => {
            const video = s.querySelector('video');
            if (video) {
              video.play().catch(() => {});
            }
          });
        });

        const closeLightbox = () => {
          // Pause all videos
          overlay.querySelectorAll('video').forEach(v => { v.pause(); });
          overlay.remove();
          document.body.style.overflow = '';
        };

        const zoomInSvg = `<svg width="20" height="20" viewBox="0 0 20 20" fill="none"><circle cx="9" cy="9" r="6" stroke="currentColor" stroke-width="1.5"/><path d="M13.5 13.5L17 17" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/><path d="M9 6.5V11.5M6.5 9H11.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>`;
        const zoomOutSvg = `<svg width="20" height="20" viewBox="0 0 20 20" fill="none"><circle cx="9" cy="9" r="6" stroke="currentColor" stroke-width="1.5"/><path d="M13.5 13.5L17 17" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/><path d="M6.5 9H11.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>`;

        const toggleZoom = () => {
          const activeSlideEl = allSlides[currentIndex];
          if (!activeSlideEl || activeSlideEl.dataset.type === 'video') return;

          isZoomed = !isZoomed;
          if (isZoomed) {
            slider.classList.add('is-zoomed');
            activeSlideEl.classList.add('is-zoomed');
            magnifyBtn.innerHTML = zoomOutSvg;
            // Center the zoomed image
            requestAnimationFrame(() => {
              activeSlideEl.scrollLeft = (activeSlideEl.scrollWidth - activeSlideEl.clientWidth) / 2;
              activeSlideEl.scrollTop = (activeSlideEl.scrollHeight - activeSlideEl.clientHeight) / 2;
            });
          } else {
            slider.classList.remove('is-zoomed');
            activeSlideEl.classList.remove('is-zoomed');
            magnifyBtn.innerHTML = zoomInSvg;
          }
        };

        closeBtn.addEventListener('click', closeLightbox);
        magnifyBtn.addEventListener('click', toggleZoom);

        // Swipe navigation in level 1 (not zoomed)
        slider.addEventListener('touchstart', (e) => {
          if (isZoomed) {
            // Pan mode
            isDragging = true;
            const activeSlideEl = allSlides[currentIndex];
            startX = e.touches[0].clientX;
            startY = e.touches[0].clientY;
            scrollLeftStart = activeSlideEl.scrollLeft;
            scrollTopStart = activeSlideEl.scrollTop;
            return;
          }
          swipeStartX = e.touches[0].clientX;
          swipeStartY = e.touches[0].clientY;
          swiping = true;
        }, { passive: true });

        slider.addEventListener('touchmove', (e) => {
          if (isZoomed && isDragging) {
            const activeSlideEl = allSlides[currentIndex];
            const dx = e.touches[0].clientX - startX;
            const dy = e.touches[0].clientY - startY;
            activeSlideEl.scrollLeft = scrollLeftStart - dx;
            activeSlideEl.scrollTop = scrollTopStart - dy;
            return;
          }
        }, { passive: true });

        slider.addEventListener('touchend', (e) => {
          isDragging = false;

          if (isZoomed) {
            // Check for double-tap in zoomed mode
            const now = Date.now();
            if (now - lastTap < 350) {
              e.preventDefault();
              toggleZoom();
            }
            lastTap = now;
            return;
          }

          if (!swiping) return;
          swiping = false;
          const endX = e.changedTouches[0].clientX;
          const endY = e.changedTouches[0].clientY;
          const dx = endX - swipeStartX;
          const dy = endY - swipeStartY;

          // Only swipe if horizontal movement > vertical and > 50px
          if (Math.abs(dx) > Math.abs(dy) && Math.abs(dx) > 50) {
            if (dx < 0 && currentIndex < allSlides.length - 1) {
              currentIndex++;
            } else if (dx > 0 && currentIndex > 0) {
              currentIndex--;
            }
            slider.scrollTo({ left: currentIndex * slider.clientWidth, behavior: 'smooth' });
          }

          // Check for double-tap to zoom
          const now = Date.now();
          if (now - lastTap < 350 && Math.abs(dx) < 10) {
            e.preventDefault();
            toggleZoom();
          }
          lastTap = now;
        });

        // Mouse: double-click to zoom
        slider.addEventListener('dblclick', (e) => {
          e.preventDefault();
          toggleZoom();
        });

        // Mouse drag to pan when zoomed
        slider.addEventListener('mousedown', (e) => {
          if (!isZoomed) return;
          isDragging = true;
          const activeSlideEl = allSlides[currentIndex];
          startX = e.clientX;
          startY = e.clientY;
          scrollLeftStart = activeSlideEl.scrollLeft;
          scrollTopStart = activeSlideEl.scrollTop;
          slider.style.cursor = 'grabbing';
          e.preventDefault();
        });

        slider.addEventListener('mousemove', (e) => {
          if (!isDragging || !isZoomed) return;
          const activeSlideEl = allSlides[currentIndex];
          const dx = e.clientX - startX;
          const dy = e.clientY - startY;
          activeSlideEl.scrollLeft = scrollLeftStart - dx;
          activeSlideEl.scrollTop = scrollTopStart - dy;
        });

        slider.addEventListener('mouseup', () => {
          isDragging = false;
          slider.style.cursor = '';
        });

        // ESC to close
        const escHandler = (e) => {
          if (e.key === 'Escape') {
            closeLightbox();
            document.removeEventListener('keydown', escHandler);
          }
        };
        document.addEventListener('keydown', escHandler);

        // Left/right arrow keys to navigate
        const keyNav = (e) => {
          if (isZoomed) return;
          if (e.key === 'ArrowLeft' && currentIndex > 0) {
            currentIndex--;
            slider.scrollTo({ left: currentIndex * slider.clientWidth, behavior: 'smooth' });
          } else if (e.key === 'ArrowRight' && currentIndex < allSlides.length - 1) {
            currentIndex++;
            slider.scrollTo({ left: currentIndex * slider.clientWidth, behavior: 'smooth' });
          }
        };
        document.addEventListener('keydown', keyNav);
        overlay.addEventListener('remove', () => document.removeEventListener('keydown', keyNav));
      }
    }
  );
}

/* ============================================
   VARIANT IMAGE DROPDOWN — global initialization
   ============================================ */

document.addEventListener('DOMContentLoaded', () => {
  initVariantImageDropdowns();
});

// Close on outside click — bound once globally
document.addEventListener('click', (e) => {
  if (!e.target.closest('.variant-image-dropdown')) {
    closeAllVariantDropdowns();
  }
});

function initVariantImageDropdowns(container) {
  const scope = container || document;
  scope.querySelectorAll('.variant-image-dropdown').forEach((dropdown) => {
    // Guard against double-init
    if (dropdown.dataset.initialized === 'true') return;
    dropdown.dataset.initialized = 'true';

    const toggle = dropdown.querySelector('.variant-image-dropdown__toggle');
    const list = dropdown.querySelector('.variant-image-dropdown__list');

    if (!toggle || !list) return;

    const showPrices = dropdown.dataset.showPrices === 'true';

    toggle.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      const isOpen = toggle.getAttribute('aria-expanded') === 'true';
      closeAllVariantDropdowns();
      if (!isOpen) {
        toggle.setAttribute('aria-expanded', 'true');
        list.classList.add('is-open');

        // Scroll selected item into view
        const selected = list.querySelector('.is-selected');
        if (selected) {
          selected.scrollIntoView({ block: 'nearest' });
        }
      }
    });

    list.querySelectorAll('.variant-image-dropdown__option').forEach((option) => {
      option.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();

        const value = option.dataset.value;

        // Update selected state in the dropdown UI
        list.querySelectorAll('.variant-image-dropdown__option').forEach((o) => {
          o.classList.remove('is-selected');
          o.setAttribute('aria-selected', 'false');
        });
        option.classList.add('is-selected');
        option.setAttribute('aria-selected', 'true');

        // Update toggle display
        const isUnavailable = option.classList.contains('is-unavailable');
        toggle.classList.toggle('is-unavailable', isUnavailable);
        const toggleSelected = toggle.querySelector('.variant-image-dropdown__selected');
        if (toggleSelected) {
          const imgEl = option.querySelector('.variant-image-dropdown__thumb');
          const textEl = option.querySelector('.variant-image-dropdown__text');
          const priceEl = option.querySelector('.variant-image-dropdown__price');
          let selectedHtml = '';
          if (imgEl) {
            selectedHtml += `<img class="variant-image-dropdown__thumb" src="${imgEl.src}" alt="${value}" width="40" height="40" loading="lazy">`;
          }
          if (textEl) {
            selectedHtml += `<span class="variant-image-dropdown__text">${textEl.innerHTML}</span>`;
          }
          toggleSelected.innerHTML = selectedHtml;

          if (showPrices) {
            let togglePrice = toggle.querySelector('.variant-image-dropdown__price');
            if (priceEl) {
              if (!togglePrice) {
                togglePrice = document.createElement('span');
                togglePrice.className = 'variant-image-dropdown__price';
                const arrow = toggle.querySelector('.svg-wrapper');
                toggle.insertBefore(togglePrice, arrow);
              }
              togglePrice.innerHTML = priceEl.innerHTML;
              togglePrice.classList.toggle('is-on-sale', priceEl.classList.contains('is-on-sale'));
            } else if (togglePrice) {
              togglePrice.remove();
            }
          }
        }

        // Immediately update the displayed price from the variant price map
        const variantId = option.dataset.variantId;
        if (variantId) {
          const sectionEl = dropdown.closest('product-info') || dropdown.closest('[data-section]');
          const sectionId = sectionEl?.dataset?.section;
          const priceMapEl = document.getElementById(`VariantPriceMap-${sectionId}`);
          if (priceMapEl) {
            try {
              const priceMap = JSON.parse(priceMapEl.textContent);
              const variantPricing = priceMap[variantId];
              if (variantPricing) {
                const priceContainer = sectionEl?.querySelector(`#price-${sectionId}`);
                if (priceContainer) {
                  const priceEl = priceContainer.querySelector('.price');
                  const isSale = variantPricing.compare_at_price && variantPricing.compare_at_price > variantPricing.price;

                  if (priceEl) {
                    priceEl.classList.toggle('price--on-sale', isSale);
                    const regularPrice = priceEl.querySelector('.price__regular .price-item--regular');
                    if (regularPrice) {
                      regularPrice.innerHTML = isSale ? variantPricing.compare_at_price_formatted : variantPricing.price_formatted;
                    }
                    const saleContainer = priceEl.querySelector('.price__sale');
                    if (saleContainer) {
                      const comparePrice = saleContainer.querySelector('.price-item--regular');
                      const salePrice = saleContainer.querySelector('.price-item--sale');
                      if (comparePrice) comparePrice.innerHTML = isSale ? variantPricing.compare_at_price_formatted : '';
                      if (salePrice) salePrice.innerHTML = variantPricing.price_formatted;
                    }
                    const saleBadge = priceContainer.querySelector('.badge--sale');
                    if (saleBadge) {
                      saleBadge.classList.toggle('hidden', !isSale);
                    }
                  }
                }
              }
            } catch (e) {
              // Price map parse failed, will be updated by fetch
            }
          }
        }

        closeAllVariantDropdowns();

        // Update hidden select for form submission (but do NOT dispatch change on it)
        const liveSelect = dropdown.closest('.select')?.querySelector('select')
          || dropdown.closest('.product-form__input')?.querySelector('select');
        if (liveSelect) {
          const targetIdx = Array.from(liveSelect.options).findIndex(opt => opt.value === value);
          if (targetIdx >= 0) {
            Array.from(liveSelect.options).forEach(opt => opt.removeAttribute('selected'));
            liveSelect.selectedIndex = targetIdx;
            liveSelect.options[targetIdx].setAttribute('selected', 'selected');
          }
        }

        // Bypass hidden select change event entirely — publish variant change directly
        // to Dawn's PubSub. This avoids all iOS WebKit quirks with hidden selects.
        const variantSelects = dropdown.closest('variant-selects');
        if (variantSelects && typeof publish !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
          // Collect option value IDs from all custom dropdowns + any swatch/button pickers
          const selectedOptionValues = [];
          variantSelects.querySelectorAll('.variant-image-dropdown__option.is-selected').forEach(sel => {
            if (sel.dataset.optionValueId) selectedOptionValues.push(sel.dataset.optionValueId);
          });
          variantSelects.querySelectorAll('fieldset input:checked').forEach(inp => {
            if (inp.dataset.optionValueId) selectedOptionValues.push(inp.dataset.optionValueId);
          });

          publish(PUB_SUB_EVENTS.optionValueSelectionChange, {
            data: {
              event: { target: option },
              target: option,
              selectedOptionValues: selectedOptionValues,
            },
          });
        }
      });
    });

    // Keyboard navigation
    toggle.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        toggle.click();
      } else if (e.key === 'Escape') {
        closeAllVariantDropdowns();
      }
    });

    list.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        closeAllVariantDropdowns();
        toggle.focus();
      }
    });

    // Sort controls (above the dropdown, inside .product-form__input)
    const sortControls = dropdown.closest('.product-form__input')?.querySelector('.variant-sort-controls');
    if (sortControls) {
      // Store original order
      const originalOrder = Array.from(list.querySelectorAll('.variant-image-dropdown__option'));

      // Extract size from variant text (e.g. "SAP0822 - 5.2ct" -> 5.2)
      function extractSize(optionEl) {
        const text = optionEl.querySelector('.variant-image-dropdown__text')?.textContent || '';
        const match = text.match(/([\d.]+)\s*(?:ct|g|grams?)\b/i);
        return match ? parseFloat(match[1]) : 0;
      }

      function sortOptions(sortType) {
        const options = Array.from(list.querySelectorAll('.variant-image-dropdown__option'));

        let sorted;
        switch (sortType) {
          case 'price-asc':
            sorted = options.sort((a, b) => (parseInt(a.dataset.variantPrice) || 0) - (parseInt(b.dataset.variantPrice) || 0));
            break;
          case 'price-desc':
            sorted = options.sort((a, b) => (parseInt(b.dataset.variantPrice) || 0) - (parseInt(a.dataset.variantPrice) || 0));
            break;
          case 'size-asc':
            sorted = options.sort((a, b) => extractSize(a) - extractSize(b));
            break;
          case 'size-desc':
            sorted = options.sort((a, b) => extractSize(b) - extractSize(a));
            break;
          default:
            sorted = originalOrder;
        }

        sorted.forEach((opt) => list.appendChild(opt));
      }

      sortControls.querySelectorAll('.variant-sort__btn').forEach((btn) => {
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();

          // Update active state
          sortControls.querySelectorAll('.variant-sort__btn').forEach((b) => b.classList.remove('is-active'));
          btn.classList.add('is-active');

          // Store the active sort globally so it persists across re-inits
          window.__variantSortState = btn.dataset.sort;

          sortOptions(btn.dataset.sort);
        });
      });

      // Re-apply saved sort state after re-init (e.g. after variant change)
      if (window.__variantSortState && window.__variantSortState !== 'default') {
        sortOptions(window.__variantSortState);
        // Update active button to match
        sortControls.querySelectorAll('.variant-sort__btn').forEach((b) => b.classList.remove('is-active'));
        const activeBtn = sortControls.querySelector(`[data-sort="${window.__variantSortState}"]`);
        if (activeBtn) activeBtn.classList.add('is-active');
      }
    }
  });
}

function closeAllVariantDropdowns() {
  document.querySelectorAll('.variant-image-dropdown:not(.customization-color-dropdown)').forEach((dropdown) => {
    const toggle = dropdown.querySelector('.variant-image-dropdown__toggle');
    const list = dropdown.querySelector('.variant-image-dropdown__list');
    if (toggle) toggle.setAttribute('aria-expanded', 'false');
    if (list) list.classList.remove('is-open');
  });
}
